// audioNormalizer.js - Нормализация громкости всех источников звука
import * as Tone from 'tone';

class AudioNormalizer {
    constructor() {
        this.masterCompressor = null;
        this.masterLimiter = null;
        this.masterGain = null;
        this.targetLevel = -12; // Целевой уровень в dB (примерно -12 dB для комфортного прослушивания)
        this.isInitialized = false;
    }

    initialize() {
        if (this.isInitialized) return;

        try {
            // Создаем мастер-компрессор для нормализации динамики
            this.masterCompressor = new Tone.Compressor({
                threshold: -24,
                ratio: 4,
                attack: 0.003,
                release: 0.25,
                knee: 30
            });

            // Создаем лимитер для предотвращения клиппинга
            this.masterLimiter = new Tone.Limiter({
                threshold: -1
            });

            // Создаем мастер-гейн для финальной регулировки уровня
            this.masterGain = new Tone.Gain(1.0);

            // Подключаем цепочку: compressor -> limiter -> gain -> destination
            this.masterCompressor.connect(this.masterLimiter);
            this.masterLimiter.connect(this.masterGain);
            this.masterGain.toDestination();

            this.isInitialized = true;
            console.log('Audio normalizer initialized');
        } catch (error) {
            console.error('Error initializing audio normalizer:', error);
        }
    }

    // Получить входной узел для подключения Tone.js источников
    getInput() {
        if (!this.isInitialized) {
            this.initialize();
        }
        // Проверяем, что masterCompressor создан и является валидным AudioNode
        if (!this.masterCompressor) {
            console.warn('AudioNormalizer masterCompressor not initialized, returning null');
            return null;
        }
        
        // Дополнительная проверка: убеждаемся, что узел не был disposed
        // Tone.js узлы после dispose теряют свойство context
        if (!this.masterCompressor.context) {
            console.warn('AudioNormalizer masterCompressor was disposed, reinitializing...');
            this.isInitialized = false;
            this.initialize();
            if (!this.masterCompressor || !this.masterCompressor.context) {
                console.warn('AudioNormalizer failed to reinitialize, returning null');
                return null;
            }
        }
        
        // Проверяем, что это действительно Tone.js узел с методом connect
        if (typeof this.masterCompressor.connect !== 'function') {
            console.warn('AudioNormalizer masterCompressor is not a valid Tone.js node, returning null');
            return null;
        }
        
        return this.masterCompressor;
    }
    
    // Получить входной узел для Web Audio API источников (гитара)
    getWebAudioInput(audioContext = null) {
        if (!this.isInitialized) {
            this.initialize(audioContext);
        }
        // Возвращаем внутренний узел Tone.js, который можно подключить из Web Audio API
        // Tone.js Gain имеет внутренний входной узел, к которому можно подключиться
        if (this.webAudioInput && this.webAudioInput.input) {
            return this.webAudioInput.input;
        }
        // Fallback: возвращаем сам узел, если input недоступен
        return this.webAudioInput;
    }

    // Настройка уровней для разных источников
    setSourceLevel(source, targetLevelDb = null) {
        if (!source) return;

        const level = targetLevelDb !== null ? targetLevelDb : this.targetLevel;
        
        // Если источник уже имеет volume/gain, настраиваем его
        if (source.volume !== undefined) {
            source.volume.value = Tone.dbToGain(level);
        } else if (source.gain !== undefined) {
            source.gain.value = Tone.dbToGain(level);
        }
    }

    // Нормализация фоновой музыки
    normalizeBackgroundMusic(player) {
        if (!player) return;
        this.setSourceLevel(player, -12); // Фоновая музыка: -12 dB
    }

    // Нормализация семплера
    normalizeSampler(sampler) {
        if (!sampler) return;
        this.setSourceLevel(sampler, -12); // Семплер: -12 dB
    }

    // Нормализация гитары (уже обработанной эффектами)
    normalizeGuitar(outputGainNode) {
        if (!outputGainNode) return;
        // Гитара уже имеет outputGain, настраиваем его на целевой уровень
        if (outputGainNode.gain) {
            // Устанавливаем уровень так, чтобы после всех эффектов он был примерно -12 dB
            // Увеличиваем громкость гитары на 5% (+0.42 dB)
            const baseLevel = -12; // Базовый уровень в dB
            const guitarBoost = 0.84 * 25; // +5% ≈ +0.42 dB
            outputGainNode.gain.value = Tone.dbToGain(baseLevel + guitarBoost);
        }
    }

    // Установка общего уровня нормализации
    setTargetLevel(levelDb) {
        this.targetLevel = levelDb;
        if (this.masterGain) {
            this.masterGain.gain.value = Tone.dbToGain(levelDb);
        }
    }

    dispose() {
        if (this.masterCompressor) {
            this.masterCompressor.dispose();
            this.masterCompressor = null;
        }
        if (this.masterLimiter) {
            this.masterLimiter.dispose();
            this.masterLimiter = null;
        }
        if (this.masterGain) {
            this.masterGain.dispose();
            this.masterGain = null;
        }
        this.isInitialized = false;
    }
}

// Создаем и экспортируем глобальный экземпляр
export const audioNormalizer = new AudioNormalizer();

